import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Button, { ButtonSizes, ButtonThemes } from "../../../ui-components/button/button";
import DownloadPDF from "../../download-invoice-pdf/downloadPDF";
import { isInvoicePaymentAlreadyBlocked, startPaymentFlow } from "../../../slices/payment/paymentSlice";
import { AgingInvoicesState, IState } from "../../../slices";
import { agingInvoicesSelector } from "../../../slices/aging/agingInvoicesSlice";
import { goToPaymentStatus } from "../../../app/data/payment/paymentUtils";
import PendingPaymentNotice from "../invoice-payment/pendingPaymentNotice";

interface InvoicesSelectedBarProps {
  selectedInvoices: number[];
  onReset: () => void;
}

const AgingInvoicesSelectedBar: React.FC<InvoicesSelectedBarProps> = (props) => {
  const agingInvoicesState: AgingInvoicesState = useSelector(agingInvoicesSelector);
  const dispatch = useDispatch();
  const history = useHistory();
  const blockedInvoices = useSelector((state: IState) => state.payment.blockedInvoices);

  const isPendingInvoice = (invoiceNumber: number) => {
    const invoice = agingInvoicesState.invoices.find((i) => i.invoiceNumber === invoiceNumber);
    return !!invoice?.pendingPayment;
  };

  const getInvoiceNumbers = () => {
    let invoiceNumbers: number[] = [];
    if (!!props.selectedInvoices?.length) {
      let filteredInvoiceNumbers = props.selectedInvoices.filter(
        (invoiceNumber) =>
          !blockedInvoices[invoiceNumber.toString()] &&
          !isInvoicePaymentAlreadyBlocked(invoiceNumber.toString()) &&
          !isPendingInvoice(invoiceNumber)
      );
      invoiceNumbers = [...filteredInvoiceNumbers];
    } else {
      invoiceNumbers = agingInvoicesState.invoices
        .filter((invoice) => !invoice.paid)
        .filter(
          (invoice) =>
            !blockedInvoices[invoice.invoiceNumber.toString()] &&
            !isInvoicePaymentAlreadyBlocked(invoice.invoiceNumber.toString()) &&
            !invoice.pendingPayment
        )
        .map((invoice) => invoice.invoiceNumber);
    }
    return invoiceNumbers;
  };

  const getPaymentSum = () => {
    let invoiceNumbers: number[] = getInvoiceNumbers();
    const paymentSum =
      agingInvoicesState.invoices.length > 0
        ? agingInvoicesState.invoices
            .filter((invoice) => invoiceNumbers.indexOf(invoice.invoiceNumber) >= 0)
            .map((invoice) => invoice.openAmount)
            .reduce((sum, curr) => sum + curr, 0)
        : undefined;
    return paymentSum;
  };

  const pay = async () => {
    let invoiceNumbers: number[] = getInvoiceNumbers();
    dispatch(
      startPaymentFlow({
        invoiceNumbers,
        paymentSum: getPaymentSum(),
        initiator: "list",
      })
    );
  };

  const paymentSum = getPaymentSum();
  const maxInvoices = props.selectedInvoices?.length > 25;
  const validInvoiceNumbers = getInvoiceNumbers();

  const hasBlockedSelected = props.selectedInvoices.some(
    (invoiceNumber) =>
      blockedInvoices[invoiceNumber.toString()] ||
      isInvoicePaymentAlreadyBlocked(invoiceNumber.toString())
  );

  const pendingSelected = props.selectedInvoices
    .map((n) => agingInvoicesState.invoices.find((i) => i.invoiceNumber === n))
    .filter((i) => i?.pendingPayment);

  const isBlocked = hasBlockedSelected && validInvoiceNumbers.length === 0;
  const onlyPending =
    props.selectedInvoices.length > 0 &&
    validInvoiceNumbers.length === 0 &&
    pendingSelected.length > 0;

  return (
    <div className="xgs-aging-invoices__selected-bar">
      {maxInvoices && (
        <div className="xgs-aging-invoices__selected-bar__warning">
          You cannot pay more than 25 invoices at the same time.
        </div>
      )}
      {pendingSelected[0]?.pendingPayment?.paymentId && (
        <PendingPaymentNotice
          title={`${pendingSelected.length} invoice${pendingSelected.length > 1 ? "s" : ""} have a payment in progress`}
          text="Open the payment to see whether it completed, failed, or is still processing."
          onCheckStatus={() =>
            goToPaymentStatus(history, pendingSelected[0]!.pendingPayment!.paymentId)
          }
        />
      )}
      {isBlocked && !onlyPending && (
        <div className="xgs-aging-invoices__selected-bar__warning" style={{ color: "red" }}>
          Your payment session expired. Please contact service desk at (844) 947-7447 for
          assistance before retrying.
        </div>
      )}
      <div className="xgs-aging-invoices__selected-bar__content">
        <div className="xgs-aging-invoices__selected-bar__item xgs-aging-invoices__mobile-hidden">
          <div>
            <span className="xgs-aging-invoices__selected-bar__summary-value">Selection Summary</span>
          </div>
          <div>
            <span className="xgs-aging-invoices__selected-bar__summary-label">Invoices:</span>
            &nbsp;
            <span className="xgs-aging-invoices__selected-bar__summary-value">
              {getInvoiceNumbers().length}
            </span>
          </div>
          {!!paymentSum && (
            <div>
              <span className="xgs-aging-invoices__selected-bar__summary-label">Total&nbsp;Amount:</span>
              &nbsp;
              <span className="xgs-aging-invoices__selected-bar__summary-value">
                $&nbsp;{paymentSum.toFixed(2)}
              </span>
            </div>
          )}
        </div>
        <div className="xgs-aging-invoices__selected-bar__item">
          {!!props.selectedInvoices?.length && (
            <Button
              className="xgs-aging-invoices__mobile-hidden"
              theme={ButtonThemes.gray}
              onClick={props.onReset}
            >
              Clear Selection
            </Button>
          )}
          <DownloadPDF invoiceNumbers={props.selectedInvoices} type="multiple" />
          <Button
            className="xgs-aging-invoices__selected-bar__pay-button"
            theme={ButtonThemes.blue}
            size={ButtonSizes.autoMedium}
            onClick={pay}
            disabled={maxInvoices || isBlocked || onlyPending || !validInvoiceNumbers.length}
          >
            Pay Selected
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgingInvoicesSelectedBar;
